import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Table,
  Divider,
  Popconfirm,
  Button,
  Tag,
  Switch,
  Input
} from 'antd'
import _ from 'lodash'

import {
  getPrices,
  createPrice,
  updatePrice,
  deletePrice
} from '../../modules/priceManagement'
import {
  getSchedules,
  updateSchedule,
  createSchedule
} from '../../modules/priceSchedule'
import PriceForm from './components/PriceForm'
import ScheduleForm, { describeCron } from './components/ScheduleForm'

const PriceManagement = () => {
  const dispatch = useDispatch()
  const [visible, setVisible] = useState(false)
  const [formData, setFormData] = useState({})
  const [scheduleVisible, setScheduleVisible] = useState(false)
  const [scheduleData, setScheduleData] = useState({})
  const [search, setSearch] = useState('')

  const { prices, isLoading, schedules, isSaving } = useSelector(
    (state) => ({
      prices: state.priceManagement.prices,
      isLoading: state.priceManagement.isLoading,
      schedules: state.priceSchedule.schedules,
      isSaving: state.priceSchedule.isSaving
    }),
    []
  )

  useEffect(() => {
    dispatch(getPrices())
    dispatch(getSchedules())
  }, [dispatch])

  const filteredPrices = useMemo(() => {
    if (!search.trim()) return prices || []
    const q = search.trim().toLowerCase()
    return (prices || []).filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.name_en && p.name_en.toLowerCase().includes(q))
    )
  }, [prices, search])

  const onReload = () => {
    dispatch(getPrices())
    dispatch(getSchedules())
  }

  const scheduleByPriceId = {}
  ;(schedules || []).forEach((s) => {
    const key = s.price_id != null ? s.price_id : s.id
    scheduleByPriceId[key] = s
  })

  const onOpenCreate = () => {
    setFormData({})
    setVisible(true)
  }

  const onOpenEdit = (record) => {
    setFormData(record)
    setVisible(true)
  }

  const onCloseModal = () => {
    setVisible(false)
    setFormData({})
  }

  const onOk = async (data) => {
    if (!_.isEmpty(formData)) {
      await dispatch(updatePrice(data))
    } else {
      await dispatch(createPrice(data))
    }
    await dispatch(getPrices())
    setVisible(false)
    setFormData({})
  }

  const onDelete = async (id) => {
    await dispatch(deletePrice(id))
    await dispatch(getPrices())
  }

  const onOpenSchedule = (record) => {
    const schedule = scheduleByPriceId[record.id] || {}
    setScheduleData({
      ...schedule,
      price_id: record.id,
      name: record.name
    })
    setScheduleVisible(true)
  }

  const onCloseSchedule = () => {
    setScheduleVisible(false)
    setScheduleData({})
  }

  const onScheduleOk = async (data) => {
    if (!scheduleData.price_id) {
      setScheduleVisible(false)
      return
    }
    const existing = scheduleByPriceId[scheduleData.price_id]
    const payload = {
      ...data,
      is_active: existing ? existing.is_active !== false : true
    }
    if (existing) {
      await dispatch(updateSchedule(scheduleData.price_id, payload))
    } else {
      await dispatch(createSchedule(scheduleData.price_id, payload))
    }
    await dispatch(getSchedules())
    setScheduleVisible(false)
    setScheduleData({})
  }

  const onToggleScheduleActive = async (record, checked) => {
    const schedule = scheduleByPriceId[record.id]
    if (!schedule) return
    await dispatch(
      updateSchedule(record.id, {
        cron_expression: schedule.cron_expression,
        timezone: schedule.timezone,
        is_active: checked
      })
    )
    await dispatch(getSchedules())
  }

  const columns = [
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.name_en && (
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>
              {record.name_en}
            </span>
          )}
        </div>
      )
    },
    {
      title: 'Source Type',
      dataIndex: 'source_type',
      key: 'source_type',
      sorter: (a, b) =>
        (a.source_type || '').localeCompare(b.source_type || ''),
      render: (type) => <Tag>{type}</Tag>
    },
    {
      title: 'Symbol / URL',
      dataIndex: 'source_url',
      key: 'source_url',
      render: (url) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{url}</span>
      )
    },
    {
      title: 'หน่วยก่อนแปลง',
      key: 'source_pair',
      render: (text, record) => {
        const cur = record.source_currency
        const unit = record.source_unit
        if (!cur && !unit) return '-'
        return [cur, unit].filter(Boolean).join('/')
      }
    },
    {
      title: 'หน่วยหลังแปลง',
      key: 'target_pair',
      render: (text, record) => {
        const cur = record.target_currency
        const unit = record.target_unit
        if (!cur && !unit) return '-'
        return [cur, unit].filter(Boolean).join('/')
      }
    },
    {
      title: 'VAT',
      dataIndex: 'vat_rate',
      key: 'vat_rate',
      align: 'center',
      render: (v) =>
        v ? <Tag color="orange">{`VAT ${(v * 100).toFixed(0)}%`}</Tag> : '-'
    },
    {
      title: 'ตารางเวลา',
      key: 'schedule',
      render: (text, record) => {
        const schedule = scheduleByPriceId[record.id]
        if (!schedule) {
          return (
            <Button
              size="small"
              icon="clock-circle"
              onClick={() => onOpenSchedule(record)}>
              ตั้งเวลา
            </Button>
          )
        }
        return (
          <div>
            <div>{describeCron(schedule.cron_expression)}</div>
            <div style={{ marginTop: 4 }}>
              <Switch
                size="small"
                checked={!!schedule.is_active}
                loading={isSaving}
                onChange={(checked) => onToggleScheduleActive(record, checked)}
              />
              <Button
                size="small"
                icon="edit"
                style={{ marginLeft: 8 }}
                onClick={() => onOpenSchedule(record)}
              />
            </div>
          </div>
        )
      }
    },
    {
      title: 'สถานะ',
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'center',
      sorter: (a, b) => Number(!!b.is_active) - Number(!!a.is_active),
      render: (active) =>
        active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            size="small"
            shape="circle"
            icon="edit"
            onClick={() => onOpenEdit(record)}
          />
          <Divider type="vertical" />
          <Popconfirm
            title="ยืนยันการลบ?"
            onConfirm={() => onDelete(record.id)}>
            <Button type="danger" size="small" shape="circle" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <Card
      title="จัดการราคา"
      extra={
        <div>
          <Input.Search
            placeholder="ค้นหาชื่อดัชนี (ไทย / EN)"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240, marginRight: 8 }}
          />
          <Button
            size="small"
            icon="plus"
            type="primary"
            onClick={onOpenCreate}
            style={{ marginRight: 8 }}>
            เพิ่มราคา
          </Button>
          <Button size="small" icon="reload" onClick={onReload}>
            Reload
          </Button>
        </div>
      }>
      <Table
        loading={isLoading}
        scroll={{ x: 1400 }}
        rowKey={(record) => record.id}
        dataSource={filteredPrices}
        columns={columns}
        pagination={{ pageSize: 20 }}
      />
      <PriceForm
        visible={visible}
        onOk={onOk}
        onCancel={onCloseModal}
        formData={formData}
      />
      <ScheduleForm
        visible={scheduleVisible}
        onOk={onScheduleOk}
        onCancel={onCloseSchedule}
        formData={scheduleData}
      />
    </Card>
  )
}

export default PriceManagement
